import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { formatMoney, intervalLabel } from "@/lib/money";
import { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  ArrowRight, Loader2, KeyRound, ShieldOff, Trash2, Settings,
  Sparkles, CreditCard, Crown, MailWarning,
} from "lucide-react";

const fmtDate = (iso) => {
  try {
    return new Date(iso).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
  } catch {
    return iso;
  }
};

export function PlanCard({ entitlement, config, onUpgrade, onManage }) {
  const tier = entitlement?.tier || "free";
  const TIER_META = {
    comp: { label: "Complimentary", cls: "bg-violet-50 border-violet-200 text-violet-700", Icon: Crown },
    team: { label: entitlement?.team?.name ? `Team · ${entitlement.team.name}` : "Team", cls: "bg-sky-50 border-sky-200 text-sky-700", Icon: Crown },
    pro: { label: "Pro", cls: "bg-emerald-50 border-emerald-200 text-emerald-700", Icon: Sparkles },
    free: { label: "Free", cls: "bg-slate-100 border-slate-200 text-slate-600", Icon: CreditCard },
  };
  const meta = TIER_META[tier] || TIER_META.free;

  return (
    <Card className={`mt-6 p-4 sm:p-5 border ${meta.cls} flex flex-wrap items-center justify-between gap-3`} data-testid="plan-card">
      <div className="flex items-center gap-3 min-w-0">
        <div className="w-10 h-10 rounded-lg bg-white/70 flex items-center justify-center shrink-0">
          <meta.Icon className="w-5 h-5" />
        </div>
        <div className="min-w-0">
          <div className="text-sm font-bold" data-testid="plan-tier-label">
            {meta.label}
          </div>
          <div className="text-xs opacity-80">
            {tier === "free" && "Upgrade to unlock the full simulator suite."}
            {tier === "pro" && entitlement?.cancel_at_period_end && "Cancels at period end."}
            {tier === "pro" && !entitlement?.cancel_at_period_end && config?.pro &&
              `${formatMoney(config.pro.price_cents, config.currency)} / ${intervalLabel(config.pro.interval)}`}
            {tier === "team" && "Full access via your organisation."}
            {tier === "comp" && "Full access granted by an administrator."}
          </div>
        </div>
      </div>
      <div className="flex gap-2 shrink-0">
        {tier === "free" && (
          <Button onClick={onUpgrade} data-testid="plan-upgrade-btn" className="bg-[#0047AB] hover:bg-[#003380] text-white">
            <Sparkles className="w-4 h-4 mr-1.5" /> Upgrade to Pro
          </Button>
        )}
        {tier === "pro" && (
          <Button variant="outline" onClick={onManage} data-testid="plan-manage-btn" className="bg-white/60">
            Manage billing
          </Button>
        )}
      </div>
    </Card>
  );
}

export function SummaryStats({ count, best }) {
  const loading = count === null;
  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 mt-8">
      <Card className="border-slate-200 p-4" data-testid="stat-total">
        {loading ? (
          <Skeleton className="h-9 w-12 bg-slate-200" />
        ) : (
          <div className="text-3xl font-bold text-slate-900">{count ?? "—"}</div>
        )}
        <div className="text-xs text-slate-500 mt-0.5">Sessions saved</div>
      </Card>
      <Card className="border-slate-200 p-4" data-testid="stat-best">
        {loading ? (
          <Skeleton className="h-9 w-12 bg-slate-200" />
        ) : (
          <div className="text-3xl font-bold text-emerald-600">{best != null ? `${best}` : "—"}</div>
        )}
        <div className="text-xs text-slate-500 mt-0.5">Best score</div>
      </Card>
      <Card className="border-slate-200 p-4 hidden sm:block">
        <Link to="/sections" data-testid="dashboard-launch-link" className="flex items-center gap-1.5 text-sm font-semibold text-[#0047AB] h-full">
          Launch a simulator <ArrowRight className="w-4 h-4" />
        </Link>
      </Card>
    </div>
  );
}

export function SessionsList({ items }) {
  return (
    <div className="mt-8 space-y-3" data-testid="sessions-list">
      {items === null && (
        <div className="space-y-3" data-testid="sessions-skeleton">
          {[0, 1, 2].map((i) => (
            <Card key={i} className="border-slate-200 p-4 flex items-center justify-between gap-3">
              <div className="min-w-0 space-y-2">
                <Skeleton className="h-4 w-40 bg-slate-200" />
                <Skeleton className="h-3 w-56 bg-slate-200" />
              </div>
              <div className="flex items-center gap-4">
                <Skeleton className="h-8 w-8 bg-slate-200" />
                <Skeleton className="h-8 w-8 bg-slate-200" />
                <Skeleton className="h-9 w-28 rounded-md bg-slate-200" />
              </div>
            </Card>
          ))}
        </div>
      )}
      {items && items.length === 0 && (
        <div className="text-center text-slate-500 border-2 border-dashed border-slate-200 rounded-lg py-12" data-testid="sessions-empty">
          No saved sessions yet. Complete a simulator and hit “Save score”.
          <div className="mt-3">
            <Link to="/sections">
              <Button className="bg-[#0047AB] hover:bg-[#003380] text-white">
                Choose a simulator <ArrowRight className="w-4 h-4 ml-1.5" />
              </Button>
            </Link>
          </div>
        </div>
      )}
      {items && items.map((it) => (
        <Card key={it.id} className="border-slate-200 p-4 flex flex-wrap items-center justify-between gap-3" data-testid={`session-row-${it.id}`}>
          <div className="min-w-0">
            <div className="text-sm font-semibold text-slate-900">{it.simulator_name}</div>
            <div className="text-xs text-slate-500">
              {fmtDate(it.created_at)}{it.summary ? ` · ${it.summary}` : ""}
            </div>
          </div>
          <div className="flex items-center gap-4">
            <div className="text-right">
              <div className="text-lg font-bold text-slate-900">{Math.round(it.score)}</div>
              <div className="text-[10px] uppercase tracking-wider text-slate-400">Score</div>
            </div>
            <div className="text-right">
              <div className="text-lg font-bold text-emerald-600">{it.grade}</div>
              <div className="text-[10px] uppercase tracking-wider text-slate-400">Grade</div>
            </div>
          </div>
        </Card>
      ))}
    </div>
  );
}

export function AccountSettings({ user, changePassword, logoutAll, deleteAccount, resendVerification }) {
  const navigate = useNavigate();
  const [curPw, setCurPw] = useState("");
  const [newPw, setNewPw] = useState("");
  const [pwBusy, setPwBusy] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [delPw, setDelPw] = useState("");
  const [delBusy, setDelBusy] = useState(false);
  const [resending, setResending] = useState(false);

  const hasPassword = user?.auth_provider === "email" || user?.auth_provider === "email+google";

  const doResend = async () => {
    setResending(true);
    try {
      const res = await resendVerification();
      toast.success(res?.message || "Verification email sent. Please check your inbox.");
    } catch {
      toast.error("Could not send the verification email. Please try again shortly.");
    } finally {
      setResending(false);
    }
  };

  const doChangePassword = async (e) => {
    e.preventDefault();
    setPwBusy(true);
    try {
      await changePassword(curPw, newPw);
      toast.success("Password updated. Other sessions have been signed out.");
      setCurPw("");
      setNewPw("");
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Could not change password.");
    } finally {
      setPwBusy(false);
    }
  };

  const doLogoutAll = async () => {
    await logoutAll();
    toast.success("Signed out on all devices.");
    navigate("/login");
  };

  const doDeleteAccount = async () => {
    setDelBusy(true);
    try {
      await deleteAccount(hasPassword ? delPw : undefined);
      toast.success("Your account has been deleted.");
      navigate("/sections");
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Could not delete account.");
      setDelBusy(false);
    }
  };

  return (
    <div className="mt-12 pt-8 border-t border-slate-200" data-testid="account-settings">
      <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.18em] text-[#0047AB] font-semibold mb-2">
        <Settings className="w-3.5 h-3.5" />
        <span>Account</span>
      </div>
      <h2 className="text-lg font-bold text-slate-900">Account settings</h2>

      {user && user.email_verified === false && (
        <Card
          className="mt-4 border-amber-200 bg-amber-50 p-4 flex flex-wrap items-center justify-between gap-3"
          data-testid="verify-email-notice"
        >
          <div className="flex items-center gap-2.5 text-sm text-amber-800 min-w-0">
            <MailWarning className="w-4 h-4 shrink-0" />
            <span className="min-w-0">
              Please verify your email address — we sent a link to{" "}
              <b className="break-all">{user.email}</b>.
            </span>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={doResend}
            disabled={resending}
            data-testid="resend-verification-btn"
            className="border-amber-300 text-amber-800 hover:bg-amber-100 bg-white/70 shrink-0"
          >
            {resending && <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />} Resend email
          </Button>
        </Card>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mt-4">
        {hasPassword && (
          <Card className="border-slate-200 p-5" data-testid="change-password-card">
            <div className="flex items-center gap-2 text-sm font-semibold text-slate-900">
              <KeyRound className="w-4 h-4 text-[#0047AB]" /> Change password
            </div>
            <form onSubmit={doChangePassword} className="mt-3 space-y-3">
              <div className="space-y-1.5">
                <Label htmlFor="cur-pw" className="text-xs">Current password</Label>
                <Input id="cur-pw" type="password" value={curPw} onChange={(e) => setCurPw(e.target.value)} data-testid="current-password-input" required />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="new-pw" className="text-xs">New password</Label>
                <Input id="new-pw" type="password" value={newPw} onChange={(e) => setNewPw(e.target.value)} data-testid="new-password-input" minLength={6} required />
              </div>
              <Button type="submit" disabled={pwBusy} data-testid="change-password-submit" className="bg-[#0047AB] hover:bg-[#003380] text-white">
                {pwBusy && <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />} Update password
              </Button>
            </form>
          </Card>
        )}

        <Card className="border-slate-200 p-5 flex flex-col gap-3" data-testid="account-security-card">
          <div className="flex items-center gap-2 text-sm font-semibold text-slate-900">
            <ShieldOff className="w-4 h-4 text-slate-500" /> Sessions &amp; security
          </div>
          <p className="text-xs text-slate-500">
            Signed in via <span className="font-semibold text-slate-700">{user?.auth_provider}</span>.
            Sign out everywhere to revoke all devices.
          </p>
          <Button variant="outline" onClick={doLogoutAll} data-testid="logout-all-btn" className="text-slate-700 w-fit">
            <ShieldOff className="w-4 h-4 mr-1.5" /> Sign out everywhere
          </Button>
          <div className="border-t border-slate-100 pt-3 mt-1">
            <p className="text-xs text-slate-500 mb-2">Permanently delete your account and all saved sessions.</p>
            <Button variant="outline" onClick={() => setConfirmDelete(true)} data-testid="delete-account-btn" className="text-rose-600 border-rose-200 hover:bg-rose-50 w-fit">
              <Trash2 className="w-4 h-4 mr-1.5" /> Delete my account
            </Button>
          </div>
        </Card>
      </div>

      <AlertDialog open={confirmDelete} onOpenChange={(o) => { if (!o) { setConfirmDelete(false); setDelPw(""); } }}>
        <AlertDialogContent data-testid="delete-account-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete your account?</AlertDialogTitle>
            <AlertDialogDescription>
              This permanently removes your account and every saved session, score and grade. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          {hasPassword && (
            <div className="space-y-1.5">
              <Label htmlFor="del-pw" className="text-xs">Confirm your password</Label>
              <Input id="del-pw" type="password" value={delPw} onChange={(e) => setDelPw(e.target.value)} data-testid="delete-account-password" placeholder="••••••••" />
            </div>
          )}
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="delete-account-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={doDeleteAccount} disabled={delBusy || (hasPassword && !delPw)} data-testid="delete-account-confirm" className="bg-rose-600 hover:bg-rose-700">
              {delBusy && <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />} Delete account
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
